import { Extension, ExtensionStyle } from "@/types/dto/pdp/pdp-itinerary";
import { ColletteButton, ColletteChip } from "@collette-travel/collette-common-controls";
import { Box, Grid, Stack, Typography } from "@mui/material";
import Image from "next/image";
import React, { useState } from "react";
import { ActivityLevel } from "@/types/dto/pdp/pdp-header-about";
import { DirectionsRun, InfoOutlined } from "@mui/icons-material";
import ActivityLevelIndicator from "@/components/common/activity-level-indicator/activity-level-indicator";
import { useTranslations } from "next-intl";
import EnhanceYourTourModal from "./enhance-your-tour-modal";

interface EnhanceYourTourCardProps {
  extension?: Extension;
  handleOpenExtensionStyles: (extensionStyle: string) => void;
  handleOpenActivityLevel: (defaultLevel: ActivityLevel["number"]) => void;
  extensionStyleProps: ExtensionStyle[];
  type: "pre" | "post";
}

const EnhanceYourTourCard = ({
  extension,
  handleOpenExtensionStyles,
  handleOpenActivityLevel,
  extensionStyleProps,
  type
}: EnhanceYourTourCardProps) => {
  const t = useTranslations("PDP");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const selectedExtensionStyle = extension?.extensionStyle
    ? extensionStyleProps.find(
        (ext) => ext.name.toLowerCase() === extension.extensionStyle.toLowerCase()
      )
    : undefined;

  if (!extension) return null;

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  return (
    <Grid size={6}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: { xs: "68rem", sm: "66rem", md: "76rem" },
          width: { xs: "32rem", sm: "50rem", md: "61rem", lg: "100%" }
        }}
      >
        {/* Image */}
        {extension.imageUrl && (
          <Box
            sx={{
              borderRadius: "2rem 2rem 0 0",
              overflow: "hidden",
              width: "100%",
              height: { xs: "21.2rem", sm: "24rem", md: "37rem" },
              position: "relative",
              flexShrink: 0,
              backgroundColor: "grey.200"
            }}
          >
            <Image
              src={extension.imageUrl}
              data-testid={`${type}-tour-extension-image`}
              alt={extension.imageAltTag || "enhance-tour-image"}
              fill
              style={{ objectFit: "cover" }}
              unoptimized
            />
          </Box>
        )}
        {/* Content */}
        <Stack
          sx={{
            backgroundColor: "primary.main",
            color: "primary.contrastText",
            gap: "2.4rem",
            width: "100%",
            borderRadius: extension.imageUrl ? "0rem 0rem 2rem 2rem" : "2rem",
            padding: { xs: "3.2rem 2.4rem", md: "3.2rem" },
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between"
          }}
        >
          {/* Content */}
          <Stack sx={{ gap: "2.4rem" }}>
            <Stack sx={{ gap: "1.2rem" }}>
              <Typography
                variant="h4"
                data-testid={`${type}-tour-extension-title-block`}
                sx={{ fontSize: { xs: "2rem", md: "2.2rem", lg: "2.4rem" } }}
              >
                {t(type === "pre" ? "Pre-Tour Extension" : "Post-Tour Extension")}:{" "}
                {extension.title}
              </Typography>
              {extension.price && (
                <ColletteChip
                  testId={`${type}-tour-chip`}
                  color="warning"
                  label={t("Extension Price Label", { price: extension.price })}
                  size="medium"
                  variant="filled"
                  sx={{
                    width: "fit-content",
                    display: "flex",
                    padding: "0.6rem 0rem",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "1rem",
                    borderRadius: "1rem",
                    fontWeight: "600"
                  }}
                />
              )}
            </Stack>

            <Stack sx={{ gap: "1.2rem" }}>
              <Stack sx={{ gap: "1.6rem" }}>
                {extension.activityLevelNumber && (
                  <Stack
                    onClick={() => handleOpenActivityLevel(extension.activityLevelNumber)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleOpenActivityLevel(extension.activityLevelNumber);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={t("View activity level information")}
                    sx={{ cursor: "pointer", gap: "0.8rem", flexDirection: { sm: "row" } }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "0.8rem" }}>
                      <DirectionsRun sx={{ height: "2.4rem", width: "2.4rem" }} />
                      <Typography data-testid={`${type}-tour-extension-activity-level-block`} component="span" sx={{ fontWeight: 700 }}>
                        {t("Activity Level")}:{" "}
                      </Typography>
                    </Box>
                    <Stack
                      sx={{
                        flexDirection: { xs: "row", gap: "0.8rem", justifyContent: "flex-end" }
                      }}
                    >
                      <Typography
                        data-testid={`${type}-tour-extension-activity-level-description-block`}
                        sx={{
                          display: "flex",
                          gap: "0.4rem",
                          justifyContent: "center",
                          alignItems: "center"
                        }}
                      >
                        {extension.activityLevelDescription}{" "}
                        <InfoOutlined sx={{ height: "20px", width: "20px", marginBottom: "2px" }} />
                      </Typography>
                      <ActivityLevelIndicator
                        activityLevelNumber={extension.activityLevelNumber}
                        size="small"
                        testIdPrefix={`${type}-tour-card`}
                      />
                    </Stack>
                  </Stack>
                )}

                {extension.extensionStyle && (
                  <Stack
                    onClick={() => handleOpenExtensionStyles(extension.extensionStyle)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleOpenExtensionStyles(extension.extensionStyle);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={t("View extension style information")}
                    sx={{
                      display: "flex",
                      gap: "0.8rem",
                      flexDirection: "row",
                      alignItems: "center",
                      cursor: "pointer",
                      width: "fit-content"
                    }}
                  >
                    {selectedExtensionStyle?.imageUrl && selectedExtensionStyle?.imageAltTag && (
                      <Image
                        src={selectedExtensionStyle.imageUrl}
                        data-testid={`${type}-tour-extension-style-image-block`}
                        alt={selectedExtensionStyle.imageAltTag}
                        height={24}
                        width={24}
                        style={{ filter: "brightness(0) invert(1)" }}
                      />
                    )}
                    <Box
                      sx={{
                        fontSize: "1.6rem",
                        fontWeight: 500,
                        lineHeight: 1.5,
                        display: "flex",
                        gap: "6px"
                      }}
                    >
                      <Typography data-testid={`${type}-tour-extension-style-label`} component="span" sx={{ fontWeight: 700 }}>
                        {t("Extension Style")}:{" "}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{ display: "flex", alignItems: "center", gap: "6px" }}
                        data-testid={`${type}-tour-extension-style-block`}
                      >
                        {extension.extensionStyle}
                        <InfoOutlined data-testid={`${type}-info-icon`} sx={{ height: "20px", width: "20px", marginBottom: "2px" }} />
                      </Typography>
                    </Box>
                  </Stack>
                )}
              </Stack>
              {extension.description && (
                <Typography
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                  data-testid={`${type}-tour-extension-description-block`}
                >
                
                  {extension.description}
                </Typography>
              )}
            </Stack>
          </Stack>

          {/* Button */}
          <ColletteButton
            color="primary"
            label={t("View Details")}
            onClick={handleOpenModal}
            size="large"
            testId={`${type}-tour-view-details-button`}
            sx={{
              backgroundColor: "background.default",
              color: "primary.main",
              width: { xs: "100%", md: "fit-content" },
              "&:hover": {
                backgroundColor: "background.warm"
              },
              "&:focus-visible": {
                outline: "2px solid white",
                outlineOffset: "1px"
              }
            }}
          />
        </Stack>
      </Box>

      {/* View Details Modal */}
      <EnhanceYourTourModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        extension={extension}
        selectedExtensionStyle={selectedExtensionStyle}
        type={type}
      />
    </Grid>
  );
};

export default EnhanceYourTourCard;
